import React, { useEffect, useState } from 'react';
import { getCredits, getHistory, getMe } from '../api/endpoints';
import { AuthImage } from '../components/AuthImage';
import { useAuthImage } from '../hooks/useAuthImage';
import Loading from '../components/Loading';
import EmptyState from '../components/EmptyState';
import { type Credits, type HistoryItem, type Job, type PremiumJob, type HairAnalysisJob, type MakeupAnalysisJob, type ScanType } from '../types/api';
import { navigateTo } from '../utils/navigation';
import { motion, useReducedMotion, type Variants, type Transition } from 'framer-motion';

type User = { name?: string; surname?: string; email?: string; avatar?: string };

const EASE: [number, number, number, number] = [0.22, 0.61, 0.36, 1];

export default function HomePage(): React.JSX.Element {
  const [credits, setCredits] = useState<Credits | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const prefersReduced = useReducedMotion();

  const spring: Transition = prefersReduced
    ? { duration: 0 }
    : { type: 'spring', stiffness: 380, damping: 34, mass: 0.95 };
  const quick: Transition = prefersReduced ? { duration: 0 } : { duration: 0.18, ease: EASE };
  const base:  Transition = prefersReduced ? { duration: 0 } : { duration: 0.32, ease: EASE };

  const container: Variants = {
    hidden: {},
    visible: {
      transition: { staggerChildren: prefersReduced ? 0 : 0.07, delayChildren: prefersReduced ? 0 : 0.04 },
    },
  };
  const item: Variants = {
    hidden: { opacity: 0, y: 6 },
    visible: { opacity: 1, y: 0, transition: base },
  };

  useEffect(() => {
    void Promise.all([
      getCredits().then(setCredits).catch(() => { setCredits(null); }),
      getHistory({ limit: 10 }).then((res) => { setHistory(res.items); }),
      getMe().then(setUser).catch(() => { setUser(null); }),
    ]).finally(() => { setLoading(false); });
  }, []);

  // Hook must run on every render (before any early return).
  const { src: avatarSrc } = useAuthImage(user?.avatar ? user.avatar.replace(/^\.\/+/, '') : null);

  if (loading) return <Loading />;

  // The "Last analysis" hero is a single-image layout built for face/hair/
  // makeup/premium scans. Product comparison/compatibility scans (two images,
  // no `image_path`) are excluded here — they live in History, not this hero.
  type FaceHistoryItem = {
    job_type: Exclude<ScanType, 'comparison' | 'compatibility'>;
    job: Job | PremiumJob | HairAnalysisJob | MakeupAnalysisJob;
  };
  const isDoneFaceItem = (r: HistoryItem): r is FaceHistoryItem =>
    r.job.status === 'done' && r.job_type !== 'comparison' && r.job_type !== 'compatibility';
  const itemRec = history.find(isDoneFaceItem) ?? null;

  type AnalysisDisplay = {
    badgeText: string;
    badgeVariant: 'teal' | 'coral' | '';
    heading: string;
    advice: string;
    navigatePath: string;
  };

  const getAnalysisDisplay = (rec: HistoryItem): AnalysisDisplay => {
    const { job, job_type } = rec;
    if (job_type === 'hair') {
      const result = (job as HairAnalysisJob).result;
      if (!result) {
        return {
          badgeText: 'Hair', badgeVariant: 'teal', heading: 'Hair Analysis',
          advice: 'Your personalized hair profile is ready.',
          navigatePath: `/hair-analysis/${job.id}`,
        };
      }
      const heading = [result.hair_type.texture, result.hair_type.density].filter(Boolean).join(' · ') || 'Hair Analysis';
      const advice = result.best_parting
        ? `Best parting: ${result.best_parting}.${result.best_lengths[0] ? ` Ideal length: ${result.best_lengths[0].name}.` : ''}`
        : 'Your personalized hair profile is ready.';
      return { badgeText: 'Hair', badgeVariant: 'teal', heading, advice, navigatePath: `/hair-analysis/${job.id}` };
    }
    if (job_type === 'makeup') {
      const result = (job as MakeupAnalysisJob).result;
      if (!result) {
        return {
          badgeText: 'Makeup', badgeVariant: 'coral', heading: 'Makeup',
          advice: 'Your personalized makeup palette is ready.',
          navigatePath: `/makeup-analysis/${job.id}`,
        };
      }
      const heading = result.undertone.classification;
      const depthLevel = result.skin_depth.level;
      const advice = depthLevel
        ? `${depthLevel} skin depth · ${result.contrast_level.level} contrast.`.replace(/ ·\s*\./, '.').trim()
        : 'Your personalized makeup palette is ready.';
      return { badgeText: 'Makeup', badgeVariant: 'coral', heading, advice, navigatePath: `/makeup-analysis/${job.id}` };
    }
    if (job_type === 'premium') {
      const metrics = (job as PremiumJob).result?.summary_metrics;
      const heading = metrics?.skin_type ?? 'Premium';
      const advice = metrics
        ? `Face shape: ${metrics.face_shape}. Overall score: ${metrics.overall_score}/100.`
        : 'Your comprehensive skin report is ready.';
      return { badgeText: 'Premium', badgeVariant: 'teal', heading, advice, navigatePath: `/premium-analysis/${job.id}` };
    }
    // base
    const j = job as Job;
    const scores: Record<string, number> = { oily: j.skin_oily ?? 0, dry: j.skin_dry ?? 0, normal: j.skin_normal ?? 0 };
    const max = Object.keys(scores).reduce((a, b) => (scores[a] > scores[b] ? a : b));
    const skinType = max.charAt(0).toUpperCase() + max.slice(1);
    const advice =
      skinType === 'Oily' ? 'Sebum is reading elevated — a gentle cleanser and lightweight moisturiser may help.' :
      skinType === 'Dry'  ? 'Hydration looks low — a barrier-supporting cream can help lock in moisture.' :
      'Looking balanced today. Keep your routine steady.';
    return {
      badgeText: skinType, badgeVariant: skinType === 'Normal' ? 'teal' : 'coral',
      heading: skinType, advice, navigatePath: `/analyses/${job.id}`,
    };
  };

  const analysisDisplay = itemRec ? getAnalysisDisplay(itemRec) : null;

  const firstName = (() => {
    const n = (user?.name ?? '').trim();
    if (n) return n;
    const emailFirst = user?.email?.split('@')[0];
    return emailFirst ?? 'there';
  })();

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return 'Good morning';
    if (hour >= 12 && hour < 18) return 'Good afternoon';
    if (hour >= 18) return 'Good evening';
    return 'Hello'; // midnight – 4 am
  };

  const initials = (() => {
    const parts = [user?.name, user?.surname].filter(Boolean) as string[];
    if (parts.length > 0) return parts.map(p => p.trim()[0]).join('').slice(0, 2).toUpperCase();
    const e = user?.email?.[0];
    return e ? e.toUpperCase() : 'U';
  })();

  // Share of the monthly grant that has been spent (use-it-or-lose-it).
  const monthlyUsedPct = credits?.monthly_grant
    ? Math.min(100, Math.round(((credits.monthly_grant - credits.monthly_balance) / credits.monthly_grant) * 100))
    : 0;
  const nextResetLabel = credits?.next_reset_at
    ? new Date(credits.next_reset_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : '—';

  const tileHover = prefersReduced ? {} : { y: -3 };
  const tileTap   = { scale: prefersReduced ? 1 : 0.97 };

  return (
    <motion.div initial="hidden" animate="visible" variants={container} className="stack-6">

      {/* ── Greeting ─────────────────────────────────────── */}
      <motion.div variants={item} className="home-greeting">
        <div>
          <h1 className="home-greeting__heading">
            {getGreeting()}, <em>{firstName}</em>
          </h1>
          {credits && (
            <p className="t-small t-mute" style={{ margin: 'var(--s-1) 0 0' }}>
              Plan: <strong style={{ color: 'var(--ink)', fontWeight: 600 }}>{credits.plan_name}</strong>
            </p>
          )}
        </div>
        <motion.button
          onClick={() => { navigateTo('/profile'); }}
          className="avatar-nav"
          aria-label="Open profile"
          whileTap={{ scale: prefersReduced ? 1 : 0.96 }}
        >
          {avatarSrc ? (
            <motion.img
              src={avatarSrc}
              alt="avatar"
              initial={{ scale: prefersReduced ? 1 : 0.95, opacity: prefersReduced ? 1 : 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={spring}
            />
          ) : (
            <span className="avatar-nav__initials">{initials}</span>
          )}
        </motion.button>
      </motion.div>

      {/* ── Last Analysis — full-width hero ──────────────── */}
      <motion.div variants={item} className="card card--bordered card--flat" style={{ padding: 'var(--s-5)', width: '100%', margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--s-4)' }}>
          <h2 className="t-h3" style={{ margin: 0 }}>Last analysis</h2>
          {analysisDisplay && (
            <span className={`pill${analysisDisplay.badgeVariant ? ` pill--${analysisDisplay.badgeVariant}` : ''}`}>
              {analysisDisplay.badgeText}
            </span>
          )}
        </div>

        {itemRec && analysisDisplay ? (
          <motion.button
            type="button"
            onClick={() => { navigateTo(analysisDisplay.navigatePath); }}
            style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
            whileHover={tileHover}
            whileTap={tileTap}
            transition={quick}
          >
            <div style={{ display: 'flex', gap: 'var(--s-6)', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              {/* Photo */}
              <motion.div
                className="frame"
                style={{ width: 160, height: 160, flexShrink: 0 }}
                initial={{ opacity: prefersReduced ? 1 : 0, scale: prefersReduced ? 1 : 0.98 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={spring}
              >
                <AuthImage
                  path={itemRec.job.image_path}
                  lazy
                  alt="Last analysis"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </motion.div>

              {/* Info */}
              <div className="stack-3" style={{ flex: 1, minWidth: 180 }}>
                <motion.h3
                  style={{ font: '400 clamp(32px,5vw,52px)/1 var(--f-display)', margin: 0, letterSpacing: '-0.02em', color: 'var(--c-deep-teal)' }}
                  initial={{ opacity: prefersReduced ? 1 : 0, y: prefersReduced ? 0 : 4 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={spring}
                >
                  {analysisDisplay.heading}
                </motion.h3>
                <p className="t-small t-mute" style={{ margin: 0 }}>
                  {new Date(itemRec.job.submitted_at).toLocaleString()}
                </p>
                {analysisDisplay.advice && (
                  <p className="t-small t-mute" style={{ margin: 0, maxWidth: 400 }}>
                    {analysisDisplay.advice}
                  </p>
                )}
                <div style={{ paddingTop: 'var(--s-1)' }}>
                  <span className="btn btn--ghost btn--sm" style={{ pointerEvents: 'none' }}>
                    View full analysis
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
          </motion.button>
        ) : (
          <motion.div
            initial={{ opacity: prefersReduced ? 1 : 0 }}
            animate={{ opacity: 1 }}
            transition={quick}
            style={{ paddingBlock: 'var(--s-6)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--s-4)' }}
          >
            <EmptyState text="No analyses yet" hideOrb />
            <button onClick={() => { navigateTo('/new'); }} className="btn btn--primary btn--sm">
              Start your first analysis
            </button>
          </motion.div>
        )}
      </motion.div>

      {/* ── Quick action tiles ─────────────────────────────── */}
      <motion.div variants={item} className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {/* New analysis — deep teal */}
        <motion.button
          onClick={() => { navigateTo('/new'); }}
          style={{
            background: 'var(--c-deep-teal)', color: 'var(--c-cream)',
            borderRadius: 'var(--r-md)', padding: 'var(--s-5)',
            border: 'none', cursor: 'pointer', textAlign: 'left',
            boxShadow: 'var(--sh-soft)', width: '100%',
          }}
          whileHover={tileHover}
          whileTap={tileTap}
          transition={quick}
        >
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.12)', display: 'grid', placeItems: 'center', marginBottom: 'var(--s-3)' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/>
              <circle cx="12" cy="13" r="4"/>
            </svg>
          </div>
          <div style={{ font: '500 var(--fs-body)/1.2 var(--f-text)', marginBottom: 'var(--s-1)' }}>New analysis</div>
          <div style={{ font: '400 var(--fs-small)/1.4 var(--f-text)', opacity: 0.65 }}>Scan your skin now</div>
        </motion.button>

        {/* AI assistant — coral accent */}
        <motion.button
          onClick={() => { navigateTo('/assistant'); }}
          style={{
            background: 'rgba(244,168,142,0.15)', color: 'var(--c-charcoal)',
            borderRadius: 'var(--r-md)', padding: 'var(--s-5)',
            border: '1px solid rgba(244,168,142,0.35)', cursor: 'pointer', textAlign: 'left',
            boxShadow: 'var(--sh-soft)', width: '100%',
          }}
          whileHover={tileHover}
          whileTap={tileTap}
          transition={quick}
        >
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(244,168,142,0.25)', display: 'grid', placeItems: 'center', marginBottom: 'var(--s-3)', color: 'var(--c-error)' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2a10 10 0 0 1 10 10c0 5.52-4.48 10-10 10S2 17.52 2 12 6.48 2 12 2z"/>
              <path d="M8 12h.01M12 12h.01M16 12h.01"/>
            </svg>
          </div>
          <div style={{ font: '500 var(--fs-body)/1.2 var(--f-text)', marginBottom: 'var(--s-1)' }}>AI assistant</div>
          <div className="t-mute" style={{ font: '400 var(--fs-small)/1.4 var(--f-text)' }}>Ask Kivo anything</div>
        </motion.button>

        {/* History — neutral */}
        <motion.button
          onClick={() => { navigateTo('/history'); }}
          style={{
            background: 'var(--c-cream-weak)', color: 'var(--c-charcoal)',
            borderRadius: 'var(--r-md)', padding: 'var(--s-5)',
            border: '1px solid var(--rule)', cursor: 'pointer', textAlign: 'left',
            boxShadow: 'var(--sh-soft)', width: '100%',
          }}
          className="col-span-2 sm:col-span-1"
          whileHover={tileHover}
          whileTap={tileTap}
          transition={quick}
        >
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--c-charcoal-06)', display: 'grid', placeItems: 'center', marginBottom: 'var(--s-3)', color: 'var(--ink-muted)' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9"/>
              <path d="M12 7v5l3 2"/>
            </svg>
          </div>
          <div style={{ font: '500 var(--fs-body)/1.2 var(--f-text)', marginBottom: 'var(--s-1)' }}>History</div>
          <div className="t-mute" style={{ font: '400 var(--fs-small)/1.4 var(--f-text)' }}>All past analyses</div>
        </motion.button>
      </motion.div>

      {/* ── Usage + actions ────────────────────────────────── */}
      <motion.section variants={item} className="g-home">
        {/* Usage stats */}
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={base}
          className="card card--bordered"
          style={{ padding: 'var(--s-5)' }}
        >
          {credits ? (
            <div>
              <div className="usage-stat">
                <div className="usage-stat__header">
                  <span className="t-label">Credit balance</span>
                  <span className="pill pill--teal">{credits.total} credits</span>
                </div>
                <div className="usage-stat__count">
                  <span className="usage-stat__num">{credits.monthly_balance}</span>
                  <span className="usage-stat__denom">monthly</span>
                  <span className="usage-stat__plan">+ {credits.surplus_balance} surplus</span>
                </div>
                <div className="result-bar" role="progressbar" aria-valuenow={monthlyUsedPct} aria-valuemin={0} aria-valuemax={100} style={{ marginTop: 'var(--s-2)' }}>
                  <motion.div className="result-bar__fill" initial={{ width: 0 }} whileInView={{ width: `${monthlyUsedPct}%` }} viewport={{ once: true }} transition={spring} />
                  <div className="result-bar__gloss" />
                </div>
                <div className="usage-stat__meta">
                  <span className="t-label">{credits.monthly_balance} of {credits.monthly_grant} monthly left</span>
                  <span className="t-label">Resets {nextResetLabel}</span>
                </div>
              </div>

              <div style={{ marginTop: 'var(--s-3)' }}>
                <button onClick={() => { navigateTo('/wallet'); }} className="btn btn--ghost btn--sm">
                  Manage credits
                </button>
              </div>
            </div>
          ) : (
            <div className="result-bar" style={{ marginTop: 'var(--s-3)' }} />
          )}
        </motion.div>

        {/* Capture guide + AI info */}
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={base}
          className="card card--bordered g-home__wide"
          style={{ padding: 'var(--s-5)' }}
        >
          <div className="stack-5">
            <div className="stack-3">
              <span className="t-label">Capture guide</span>
              <p className="t-small t-mute" style={{ margin: 0 }}>
                Natural daylight, no filters, camera level with your face.
                A few small details make a big difference to accuracy.
              </p>
              <div>
                <motion.button
                  onClick={() => { navigateTo('/guide'); }}
                  className="btn btn--ghost btn--sm"
                  whileTap={{ scale: prefersReduced ? 1 : 0.98 }}
                >
                  Open guide
                </motion.button>
              </div>
            </div>

            <hr className="rule" />

            <div className="stack-3">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--s-3)' }}>
                <span className="t-label">About our AI</span>
                <span className="pill pill--teal">
                  <span aria-hidden>✓</span>
                  82% consistency
                </span>
              </div>
              <p className="t-small t-mute" style={{ margin: 0 }}>
                Training data, accuracy curves, and per-signal metrics are all visible.
              </p>
              <div className="row-3" style={{ flexWrap: 'wrap' }}>
                <motion.button onClick={() => { navigateTo('/about'); }} className="btn btn--primary btn--sm" whileTap={{ scale: prefersReduced ? 1 : 0.98 }}>
                  Learn about our AI
                </motion.button>
                <motion.button onClick={() => { navigateTo('/privacy'); }} className="btn btn--ghost btn--sm" whileTap={{ scale: prefersReduced ? 1 : 0.98 }}>
                  Privacy & data use
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.section>

    </motion.div>
  );
}
